import { Link } from "react-router-dom";
import { ArrowRight, FileText, CalendarCheck, BarChart3 } from "lucide-react";
import { MaskedLines, FadeUp } from "@/components/motion";
import { Container, SectionHeading } from "@/components/Section";
import { STEPS, IMAGES } from "@/data/content";

export default function HowItWorks() {
  return (
    <main data-testid="how-it-works-page">
      <section className="noise-dark bg-forest pb-24 pt-44 text-cream sm:pb-32 sm:pt-52">
        <Container className="relative z-10">
          <FadeUp>
            <p className="eyebrow text-sage">How it works</p>
          </FadeUp>
          <MaskedLines
            className="mt-6 max-w-4xl"
            lineClassName="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.06]"
            lines={[
              <span key="1">Live and earning,</span>,
              <span key="2" className="italic text-sage-light">without the workload.</span>,
            ]}
          />
          <FadeUp delay={0.35}>
            <p className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-cream/70">
              Four steps between where your property is now and a professionally managed short-term rental
              you barely have to think about.
            </p>
          </FadeUp>
        </Container>
      </section>

      <section className="bg-cream py-24 sm:py-32">
        <Container>
          <div className="relative space-y-0">
            {STEPS.map((s, i) => (
              <FadeUp key={s.num} delay={0.05}>
                <div
                  data-testid={`roadmap-step-${i}`}
                  className="group grid gap-6 border-t border-forest/10 py-14 last:border-b sm:grid-cols-12 sm:gap-10"
                >
                  <div className="sm:col-span-2">
                    <span className="font-serif text-6xl font-medium text-forest/15 transition-colors duration-500 group-hover:text-forest-700/50 sm:text-7xl">
                      {s.num}
                    </span>
                  </div>
                  <div className="sm:col-span-4">
                    <h2 className="font-serif text-3xl sm:text-4xl font-medium leading-tight text-forest">{s.title}</h2>
                  </div>
                  <div className="sm:col-span-6">
                    <p className="max-w-xl text-base leading-relaxed text-charcoal/70">{s.desc}</p>
                    {i === 3 && (
                      <p className="mt-5 inline-flex items-center gap-2 rounded-full bg-forest px-4 py-2 text-xs font-semibold text-cream">
                        <CalendarCheck className="h-3.5 w-3.5 text-sage" />
                        Most owners are live within a few weeks
                      </p>
                    )}
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </Container>
      </section>

      <section className="bg-bone py-24 sm:py-32">
        <Container className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <SectionHeading
              eyebrow="Owner visibility"
              title="Hands-off doesn't mean in the dark."
              copy="Every month you receive a clear statement of bookings, revenue and activity. Behind it, we track pricing performance, occupancy and guest feedback — and adjust the strategy so the property keeps improving."
            />
            <FadeUp delay={0.2}>
              <ul className="mt-8 space-y-4">
                {[
                  { icon: FileText, t: "Monthly owner statement — revenue, bookings, activity" },
                  { icon: BarChart3, t: "Ongoing pricing and occupancy performance tracking" },
                  { icon: CalendarCheck, t: "Block out your own dates anytime — it's your property" },
                ].map(({ icon: Icon, t }, i) => (
                  <li key={t} className="flex items-start gap-3.5" data-testid={`visibility-item-${i}`}>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-forest text-sage">
                      <Icon className="h-4 w-4" />
                    </span>
                    <p className="pt-1.5 text-sm font-medium text-charcoal/75">{t}</p>
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>
          <FadeUp delay={0.15} className="lg:col-span-6">
            <div className="relative">
              <div className="overflow-hidden rounded-2xl shadow-[0_30px_80px_rgba(15,38,28,0.18)]">
                <img
                  src={IMAGES.dublinGeorgian}
                  alt="Georgian architecture in Dublin"
                  className="h-80 w-full object-cover sm:h-[440px]"
                  data-testid="hiw-dublin-image"
                />
              </div>
              <div className="absolute -bottom-7 left-6 right-6 rounded-xl border border-forest/10 bg-white/90 p-5 backdrop-blur-xl sm:left-10 sm:right-auto">
                <p className="font-mono text-xs uppercase tracking-[0.18em] text-charcoal/45">This month · Sample</p>
                <div className="mt-2 flex items-baseline gap-6">
                  <div>
                    <p className="font-mono text-2xl font-semibold text-forest">87%</p>
                    <p className="text-[11px] text-charcoal/50">Occupancy</p>
                  </div>
                  <div>
                    <p className="font-mono text-2xl font-semibold text-forest">€189</p>
                    <p className="text-[11px] text-charcoal/50">Avg. nightly rate</p>
                  </div>
                </div>
              </div>
            </div>
          </FadeUp>
        </Container>
      </section>

      <section className="noise-dark bg-forest py-24 text-cream sm:py-28">
        <Container className="relative z-10 flex flex-col items-start justify-between gap-10 lg:flex-row lg:items-center">
          <div className="max-w-xl">
            <FadeUp>
              <h2 className="font-serif text-4xl sm:text-5xl font-medium">Start with step one. It takes two minutes.</h2>
              <p className="mt-5 text-base leading-relaxed text-cream/65">
                Tell us about your property and we'll take it from there.
              </p>
            </FadeUp>
          </div>
          <FadeUp delay={0.15}>
            <Link
              to="/assessment"
              data-testid="hiw-bottom-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-cream px-7 py-4 text-sm font-semibold text-forest transition-colors duration-300 hover:bg-sage"
            >
              Tell Us About Your Property
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </FadeUp>
        </Container>
      </section>
    </main>
  );
}
